import { useEffect, useRef } from "react";

// Animated node network — Protech hero signature layer.
// Gentle drift + shimmering nodes + distance-based links + occasional
// data pulses traveling along connections. Cursor-attract is subtle.
export default function NetworkCanvas({ className = "", density = 20000, interactive = true }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let nodes = [];
    let pulses = [];
    let raf;
    let running = true;
    let time = 0;
    const mouse = { x: -9999, y: -9999 };

    const init = (w, h) => {
      const count = Math.min(110, Math.max(22, Math.floor((w * h) / density)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.6 + 0.6,
        c: Math.random() > 0.5 ? "124,77,255" : "32,199,201",
        ph: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (w, h, animate) => {
      ctx.clearRect(0, 0, w, h);
      const linkDist = 130;
      const linked = [];
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDist * linkDist) {
            const base = 1 - Math.sqrt(d2) / linkDist;
            const flicker = animate ? 0.75 + 0.25 * Math.sin(time * 0.6 + a.ph + j) : 1;
            ctx.strokeStyle = `rgba(139,132,255,${base * 0.16 * flicker})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            if (animate && linked.length < 60) linked.push([a, b]);
          }
        }
      }
      nodes.forEach((p) => {
        const alpha = animate ? 0.5 + 0.35 * Math.sin(time * 0.8 + p.ph) : 0.7;
        ctx.fillStyle = `rgba(${p.c},${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      // data pulses travelling along connections
      if (animate && pulses.length < 5 && linked.length > 0 && Math.random() < 0.025) {
        const [a, b] = linked[Math.floor(Math.random() * linked.length)];
        pulses.push({
          ax: a.x, ay: a.y, bx: b.x, by: b.y,
          t: 0,
          s: 0.008 + Math.random() * 0.01,
          c: Math.random() > 0.5 ? "53,214,210" : "124,77,255",
        });
      }
      pulses = pulses.filter((p) => {
        p.t += p.s;
        if (p.t >= 1) return false;
        const x = p.ax + (p.bx - p.ax) * p.t;
        const y = p.ay + (p.by - p.ay) * p.t;
        ctx.fillStyle = `rgba(${p.c},0.25)`;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${p.c},0.95)`;
        ctx.beginPath();
        ctx.arc(x, y, 1.7, 0, Math.PI * 2);
        ctx.fill();
        return true;
      });
    };

    const step = () => {
      if (!running) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      time += 0.016;
      nodes.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const d = Math.hypot(dx, dy);
        if (d < 150 && d > 1) {
          p.x += (dx / d) * 0.2;
          p.y += (dy / d) * 0.2;
        }
      });
      draw(w, h, true);
      raf = requestAnimationFrame(step);
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      init(rect.width, rect.height);
      if (reduce) draw(rect.width, rect.height, false);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    resize();
    window.addEventListener("resize", resize);
    if (interactive) {
      canvas.parentElement.addEventListener("mousemove", onMove);
      canvas.parentElement.addEventListener("mouseleave", onLeave);
    }
    if (reduce) {
      draw(canvas.clientWidth, canvas.clientHeight, false);
    } else {
      raf = requestAnimationFrame(step);
    }

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (interactive) {
        canvas.parentElement.removeEventListener("mousemove", onMove);
        canvas.parentElement.removeEventListener("mouseleave", onLeave);
      }
    };
  }, [density, interactive]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
